// ===========================================
// DetailSinistre — Refonte UX (CRM layout : sidebar + KPIs + sections)
// ===========================================
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
    Card, Tag, Grid, Typography, Spin, Timeline, Button, Modal, Form, Input,
    InputNumber, Popconfirm, Select, Tooltip,
} from 'antd';
import {
    WarningOutlined, FileProtectOutlined, CalendarOutlined, UserOutlined,
    BankOutlined, PhoneOutlined, EnvironmentOutlined, ArrowLeftOutlined,
    FileTextOutlined, DollarOutlined, ClockCircleOutlined, EditOutlined,
    DeleteOutlined, PrinterOutlined, ShopOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';

import { sinistresService, settingsService } from '../../common/services';
import {
    formatMontant as formatMontantShared,
    formatPhone, formatDate, formatDateTime,
} from '../../common/utils/format';
import { SINISTRE_STATUTS, getSinistreStatutColor } from '../../common/constants';
import DetailSkeleton from '../../components/detailSkeleton';
import PrintHeader from '../../components/printHeader';
import PrintFooter from '../../components/printFooter';

import './detail-sinistre.page.scss';

const { Text, Title } = Typography;
const { TextArea } = Input;

const FALLBACK_STATUTS = Object.values(SINISTRE_STATUTS).map((s) => ({ code: s.code, label: s.label }));

const formatMontant = (val) => formatMontantShared(val, { fallback: '—' });

// ─── Relative date (cohérent avec le reste de l'app) ─────────────────────
const formatRelative = (date) => {
    if (!date) return '—';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '—';
    const now = new Date();
    const diffMs = now - d;
    if (diffMs < 0) return formatDate(date);

    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMs / 3600000);
    const diffDay = Math.floor(diffMs / 86400000);
    const diffMonth = Math.floor(diffDay / 30);
    const diffYear = Math.floor(diffDay / 365);

    if (diffMin < 1)    return "À l'instant";
    if (diffMin < 60)   return `Il y a ${diffMin} min`;
    if (diffHr  < 24)   return `Il y a ${diffHr} h`;
    if (diffDay < 30)   return diffDay === 1 ? 'Hier' : `Il y a ${diffDay} jours`;
    if (diffMonth < 12) return `Il y a ${diffMonth} mois`;
    return `Il y a ${diffYear} an${diffYear > 1 ? 's' : ''}`;
};

const DetailSinistre = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;

    const [sinistre, setSinistre] = useState(null);
    const [loading, setLoading] = useState(true);
    const [modalVisible, setModalVisible] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [statutForm] = Form.useForm();
    const [statutsList, setStatutsList] = useState(FALLBACK_STATUTS);

    const fetchSinistre = async () => {
        try {
            setLoading(true);
            const res = await sinistresService.getById(id);
            const data = res?.data || res;
            setSinistre(data);
        } catch (err) {
            toast.error('Erreur lors du chargement du sinistre');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchSinistre(); }, [id]);

    useEffect(() => {
        const loadStatuts = async () => {
            try {
                const res = await settingsService.getAll('sinistres');
                const raw = res?.data || res;
                const list = raw?.data || raw;
                if (Array.isArray(list)) {
                    const found = list.find((s) => s.key === 'STATUTS_SINISTRE');
                    if (found && Array.isArray(found.value) && found.value.length > 0) {
                        setStatutsList(found.value);
                    }
                }
            } catch (_) { /* */ }
        };
        loadStatuts();
    }, []);

    const handleUpdateStatut = async (values) => {
        try {
            setUpdating(true);
            await sinistresService.updateStatut(id, values);
            toast.success('Statut mis à jour');
            setModalVisible(false);
            statutForm.resetFields();
            fetchSinistre();
        } catch (err) {
            toast.error('Erreur lors de la mise à jour');
        } finally {
            setUpdating(false);
        }
    };

    if (loading) return <DetailSkeleton kpis={4} sections={2} />;

    if (!sinistre) {
        return (
            <div className="detail-sinistre-empty">
                <Title level={4}>Sinistre non trouvé</Title>
                <Button
                    type="primary"
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate('/dashboard/sinistres')}
                >
                    Retour à la liste
                </Button>
            </div>
        );
    }

    const getStatutLabel = (code) => {
        const found = statutsList.find((s) => s.code === code);
        return found ? found.label : code || '—';
    };
    const getStatutColor = (code) => getSinistreStatutColor(code);
    const statutLabel = getStatutLabel(sinistre.statut);
    const statutColor = getStatutColor(sinistre.statut);
    const canChangeStatut = sinistre.statut !== 'regle' && sinistre.statut !== 'rejete';

    const clientNom = sinistre.client?.raisonSociale
        || `${sinistre.client?.nom || ''} ${sinistre.client?.prenom || ''}`.trim()
        || '—';

    const sinistreNum = sinistre.numeroSinistre || sinistre._id?.slice(-6).toUpperCase();

    return (
        <div className="detail-sinistre-page">
            <PrintHeader
                title={`Sinistre ${sinistreNum}`}
                subtitle={`Contrat : ${sinistre.contrat?.numeroPolice || ''} — Date : ${formatDate(sinistre.dateSinistre)}`}
                agenceName={sinistre.agence?.nom}
            />

            <div className="detail-sinistre-layout">
                {/* ═════ SIDEBAR ═════ */}
                <div className="detail-sinistre-sidebar no-print">
                    <Card className="detail-sinistre-sidebar-card">
                        {/* Bouton retour */}
                        <button
                            type="button"
                            className="sidebar-back-btn"
                            onClick={() => navigate('/dashboard/sinistres')}
                            aria-label="Retour à la liste des sinistres"
                        >
                            <ArrowLeftOutlined />
                            <span>Retour à la liste des sinistres</span>
                        </button>

                        {/* Identité du sinistre */}
                        <div className="sidebar-identity">
                            <div className="sidebar-sinistre-icon">
                                <WarningOutlined />
                            </div>
                            <h1 className="sidebar-name">
                                {sinistre.contrat?.numeroPolice || '—'}
                            </h1>
                            <div className="sidebar-meta">
                                <Tag color={statutColor}>{statutLabel}</Tag>
                                {sinistre.typeSinistre && (
                                    <Tag color="orange">{sinistre.typeSinistre}</Tag>
                                )}
                            </div>
                            <div className="sidebar-since">
                                <CalendarOutlined />
                                Déclaré {formatRelative(sinistre.dateDeclaration || sinistre.createdAt)}
                            </div>
                        </div>

                        <div className="sidebar-divider" />

                        {/* Coordonnées */}
                        <div className="sidebar-coord">
                            <div className="sidebar-coord-item">
                                <div className="sidebar-coord-icon"><UserOutlined /></div>
                                <div className="sidebar-coord-body">
                                    <div className="sidebar-coord-label">Souscripteur</div>
                                    <div className="sidebar-coord-value">
                                        {sinistre.client?._id ? (
                                            <button
                                                type="button"
                                                onClick={() => navigate(`/dashboard/client/${sinistre.client._id}`)}
                                                className="sidebar-coord-link sidebar-coord-link-btn"
                                            >
                                                {clientNom}
                                            </button>
                                        ) : clientNom}
                                    </div>
                                </div>
                            </div>

                            {sinistre.client?.telephone && (
                                <div className="sidebar-coord-item">
                                    <div className="sidebar-coord-icon"><PhoneOutlined /></div>
                                    <div className="sidebar-coord-body">
                                        <div className="sidebar-coord-label">Téléphone</div>
                                        <div className="sidebar-coord-value">
                                            <a href={`tel:${sinistre.client.telephone}`} className="sidebar-coord-link">
                                                {formatPhone(sinistre.client.telephone)}
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="sidebar-coord-item">
                                <div className="sidebar-coord-icon"><BankOutlined /></div>
                                <div className="sidebar-coord-body">
                                    <div className="sidebar-coord-label">Compagnie</div>
                                    <div className="sidebar-coord-value">
                                        {sinistre.contrat?.compagnie?.nom || sinistre.contrat?.compagnie || '—'}
                                    </div>
                                </div>
                            </div>

                            <div className="sidebar-coord-item">
                                <div className="sidebar-coord-icon"><ShopOutlined /></div>
                                <div className="sidebar-coord-body">
                                    <div className="sidebar-coord-label">Agence</div>
                                    <div className="sidebar-coord-value">{sinistre.agence?.nom || '—'}</div>
                                </div>
                            </div>

                            <div className="sidebar-coord-item">
                                <div className="sidebar-coord-icon"><CalendarOutlined /></div>
                                <div className="sidebar-coord-body">
                                    <div className="sidebar-coord-label">Date du sinistre</div>
                                    <div className="sidebar-coord-value">{formatDate(sinistre.dateSinistre)}</div>
                                </div>
                            </div>
                        </div>

                        <div className="sidebar-divider" />

                        {/* Actions : 4 boutons en grille 2×2 */}
                        <div className="sidebar-actions">
                            <div className="sidebar-actions-row">
                                {canChangeStatut ? (
                                    <Button
                                        type="primary"
                                        icon={<EditOutlined />}
                                        onClick={() => setModalVisible(true)}
                                        className="sidebar-btn-statut"
                                        block
                                    >
                                        Statut
                                    </Button>
                                ) : (
                                    <Button
                                        icon={<FileProtectOutlined />}
                                        onClick={() => navigate(`/dashboard/contrat/${sinistre.contrat?._id || sinistre.contrat}`)}
                                        className="sidebar-btn-statut"
                                        block
                                    >
                                        Contrat
                                    </Button>
                                )}
                                <Button
                                    icon={<EditOutlined />}
                                    onClick={() => navigate(`/dashboard/nouveau-sinistre?edit=${id}`)}
                                    className="sidebar-btn-edit"
                                    block
                                >
                                    Modifier
                                </Button>
                            </div>
                            <div className="sidebar-actions-row">
                                <Button
                                    icon={<PrinterOutlined />}
                                    onClick={() => window.print()}
                                    className="sidebar-btn-print"
                                    block
                                >
                                    Imprimer
                                </Button>
                                <Popconfirm
                                    title="Supprimer ce sinistre ?"
                                    description="Cette action est irréversible."
                                    okText="Oui, supprimer"
                                    cancelText="Annuler"
                                    onConfirm={async () => {
                                        try {
                                            await sinistresService.delete(id);
                                            toast.success('Sinistre supprimé');
                                            navigate('/dashboard/sinistres');
                                        } catch (e) {
                                            toast.error(e.message || 'Erreur lors de la suppression');
                                        }
                                    }}
                                >
                                    <Button
                                        icon={<DeleteOutlined />}
                                        className="sidebar-btn-delete"
                                        block
                                    >
                                        Supprimer
                                    </Button>
                                </Popconfirm>
                            </div>
                        </div>
                    </Card>
                </div>

                {/* ═════ MAIN ═════ */}
                <div className="detail-sinistre-main">
                    {/* KPIs */}
                    <section className="detail-sinistre-kpis no-print" aria-label="Indicateurs sinistre">
                        <div className="detail-kpi detail-kpi-blue">
                            <div className="detail-kpi-icon"><FileProtectOutlined /></div>
                            <div className="detail-kpi-body">
                                <div className="detail-kpi-label">N° Sinistre</div>
                                <div className="detail-kpi-value">{sinistreNum}</div>
                                <div className="detail-kpi-sub">{sinistre.contrat?.numeroPolice || ''}</div>
                            </div>
                        </div>

                        <div className="detail-kpi detail-kpi-orange">
                            <div className="detail-kpi-icon"><WarningOutlined /></div>
                            <div className="detail-kpi-body">
                                <div className="detail-kpi-label">Type</div>
                                <div className="detail-kpi-value">{sinistre.typeSinistre || '—'}</div>
                                <div className="detail-kpi-sub">{formatDate(sinistre.dateSinistre)}</div>
                            </div>
                        </div>

                        <div className="detail-kpi detail-kpi-red">
                            <div className="detail-kpi-icon"><DollarOutlined /></div>
                            <div className="detail-kpi-body">
                                <div className="detail-kpi-label">Montant estimé</div>
                                <div className="detail-kpi-value">{formatMontant(sinistre.montantEstime)}</div>
                                <div className="detail-kpi-sub">déclaré</div>
                            </div>
                        </div>

                        <div className={`detail-kpi ${sinistre.montantIndemnise > 0 ? 'detail-kpi-green' : 'detail-kpi-grey'}`}>
                            <div className="detail-kpi-icon"><DollarOutlined /></div>
                            <div className="detail-kpi-body">
                                <div className="detail-kpi-label">Montant indemnisé</div>
                                <div className="detail-kpi-value">
                                    {sinistre.montantIndemnise > 0 ? formatMontant(sinistre.montantIndemnise) : '—'}
                                </div>
                                <div className="detail-kpi-sub">
                                    {sinistre.montantIndemnise > 0 ? 'versé' : 'non versé'}
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Section Détails */}
                    <Card className="detail-sinistre-card">
                        <div className="detail-sinistre-card-title">
                            <WarningOutlined /> Détails du sinistre
                        </div>

                        <div className="info-grid">
                            <div className="info-cell">
                                <div className="info-label">Type de sinistre</div>
                                <div className="info-value info-value-strong">{sinistre.typeSinistre || '—'}</div>
                            </div>
                            <div className="info-cell">
                                <div className="info-label">Date du sinistre</div>
                                <div className="info-value">{formatDate(sinistre.dateSinistre)}</div>
                            </div>
                            <div className="info-cell">
                                <div className="info-label">Statut</div>
                                <div className="info-value">
                                    <Tag color={statutColor}>{statutLabel}</Tag>
                                </div>
                            </div>
                            {sinistre.lieu && (
                                <div className="info-cell">
                                    <div className="info-label">Lieu</div>
                                    <div className="info-value">
                                        <EnvironmentOutlined /> {sinistre.lieu}
                                    </div>
                                </div>
                            )}
                            {sinistre.tiersConcerne && (
                                <div className="info-cell">
                                    <div className="info-label">Tiers concerné</div>
                                    <div className="info-value">{sinistre.tiersConcerne}</div>
                                </div>
                            )}
                            {sinistre.circonstances && (
                                <div className="info-cell info-cell-wide">
                                    <div className="info-label">Circonstances</div>
                                    <div className="info-value">{sinistre.circonstances}</div>
                                </div>
                            )}
                        </div>

                        {sinistre.description && (
                            <div className="info-description">
                                <div className="info-label">
                                    <FileTextOutlined /> Description
                                </div>
                                <div className="info-description-content">
                                    {sinistre.description}
                                </div>
                            </div>
                        )}
                    </Card>

                    {/* Historique (si présent) */}
                    {sinistre.historique && sinistre.historique.length > 0 && (
                        <Card className="detail-sinistre-card">
                            <div className="detail-sinistre-card-title">
                                <ClockCircleOutlined /> Historique du sinistre
                            </div>
                            <div className="detail-sinistre-timeline">
                                <Timeline
                                    items={[...sinistre.historique].reverse().map((h) => {
                                        const color = getStatutColor(h.statut);
                                        const tlColor = color === 'blue' ? 'blue'
                                            : color === 'orange' ? 'orange'
                                            : color === 'green' ? 'green'
                                            : 'red';
                                        return {
                                            color: tlColor,
                                            children: (
                                                <div className="timeline-item">
                                                    <div className="timeline-head">
                                                        <Tag color={color}>{getStatutLabel(h.statut)}</Tag>
                                                        <span className="timeline-date">
                                                            {formatDateTime(h.date)}
                                                        </span>
                                                    </div>
                                                    {h.commentaire && (
                                                        <div className="timeline-comment">{h.commentaire}</div>
                                                    )}
                                                    {h.par && (
                                                        <div className="timeline-author">
                                                            par {h.par.firstName || ''} {h.par.lastName || ''}
                                                        </div>
                                                    )}
                                                </div>
                                            ),
                                        };
                                    })}
                                />
                            </div>
                        </Card>
                    )}
                </div>
            </div>

            {/* Modal changement de statut */}
            <Modal
                title="Changer le statut du sinistre"
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                onOk={() => statutForm.submit()}
                confirmLoading={updating}
                okText="Confirmer"
                cancelText="Annuler"
                destroyOnClose
                style={{ top: 150 }}
            >
                <Form
                    form={statutForm}
                    layout="vertical"
                    onFinish={handleUpdateStatut}
                    style={{ marginTop: 16 }}
                >
                    <Form.Item
                        name="statut"
                        label="Nouveau statut"
                        rules={[{ required: true, message: 'Requis' }]}
                    >
                        <Select
                            placeholder="Sélectionner un statut..."
                            size="large"
                            showSearch
                            optionFilterProp="children"
                            options={statutsList
                                .filter((s) => s.code !== sinistre.statut)
                                .map((s) => ({ value: s.code, label: s.label }))}
                        />
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate={(prev, cur) => prev.statut !== cur.statut}>
                        {({ getFieldValue }) => getFieldValue('statut') === 'regle' && (
                            <Form.Item name="montantIndemnise" label="Montant indemnisé (FCFA)">
                                <InputNumber
                                    min={0}
                                    style={{ width: '100%' }}
                                    size="large"
                                    formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                    parser={(v) => v.replace(/\s/g, '')}
                                    placeholder="Montant de l'indemnisation"
                                />
                            </Form.Item>
                        )}
                    </Form.Item>
                    <Form.Item name="commentaire" label="Commentaire">
                        <TextArea
                            rows={3}
                            placeholder="Raison du changement de statut..."
                            style={{ borderRadius: 8 }}
                        />
                    </Form.Item>
                </Form>
            </Modal>

            <PrintFooter />
        </div>
    );
};

export default DetailSinistre;
